import { meStore } from '@gozoki/api'
import type { StoreCamera, StoreCameraViewSession } from '@gozoki/api-types'
import { cn } from '@gozoki/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { type PointerEvent, useEffect, useMemo, useRef, useState } from 'react'
import { pageAfterSwipe, swipeDragOffset } from './cameraSwipe'
import { Camera01Icon } from './icons/dashboard-icons'
import { ModalCloseButton } from './ui/ModalCloseButton'
import { StoreCameraPlayer } from './StoreCameraPlayer'

/** Battement de l'horloge : deux fois par minute, assez pour que l'incrustation ne retarde pas. */
const CLOCK_TICK_MS = 30_000
/** Grille plein écran : 3 colonnes × 2 lignes = exactement 6 caméras par page (Figma), sans scroll. */
const CAMERAS_PER_PAGE = 6
/** Gouttière des tuiles, reprise entre deux pages de la piste (visible pendant un glissement). */
const PAGE_GAP_PX = 24

/** Tuile d'une caméra : numéro et libellé au-dessus, flux 16:9 et horodatage incrusté. */
function CameraTile({
  camera,
  number,
  session,
  live,
  timestamp,
}: {
  camera: StoreCamera
  number: number
  session: StoreCameraViewSession | undefined
  /** Faux pour les pages voisines, visibles seulement pendant un glissement : pas de connexion. */
  live: boolean
  timestamp: string
}) {
  const renderStream = () => {
    if (!live) return null
    if (camera.fullStreamUrl && session) {
      return (
        <StoreCameraPlayer
          cameraName={camera.name}
          session={session}
          className="absolute inset-0 rounded-[16px]"
        />
      )
    }
    return (
      <span className="absolute inset-0 flex items-center justify-center font-sans text-[14px] text-white">
        Flux indisponible
      </span>
    )
  }

  return (
    <div className="flex flex-col items-center gap-[10px]">
      <p className="w-full shrink-0 truncate text-center font-brand text-[22px] leading-[26px] font-extrabold text-[#6A9560]">
        {number} - {camera.label}
      </p>

      <div className="relative aspect-video w-full overflow-hidden rounded-[16px] bg-[#2F3D28]">
        {renderStream()}

        {/* Horodatage (Figma 11393:49117) : coin haut-gauche. */}
        <span className="pointer-events-none absolute top-[16px] left-[16px] rounded-[6px] border border-black/[0.06] bg-[rgba(21,32,19,0.6)] px-[8px] py-[4px] font-sans text-[14px] leading-[16px] font-medium text-[#F9F8F1]">
          {timestamp}
        </span>
      </div>
    </div>
  )
}

/**
 * Modale « Caméras » (Figma 11393:48967) : un mur de toutes les caméras de la boutique, chacune en
 * direct dès l'ouverture (6 par page), avec son horodatage. Pas de sélection ni d'agrandissement
 * d'une caméra : l'écran de la boutique est déjà grand (demande client du 14/10). Seule la page
 * affichée joue, les caméras des autres pages ne sont pas connectées. Écran tactile : on passe d'une
 * page à l'autre en glissant le doigt (la piste suit le geste), les pastilles restent en repère.
 *
 * Rendue DANS le canvas mis à l'échelle et non via le `Dialog` partagé : celui-ci passe par un
 * portail sur `document.body`, donc hors du `transform: scale()` du dashboard — la modale
 * s'afficherait à l'échelle 1 au milieu d'une interface à ~1,5. Un `position: fixed` sous un
 * ancêtre transformé se cale sur cet ancêtre, pas sur le viewport.
 */
export function CamerasModal({
  cameras,
  session,
  initialCameraName,
  onClose,
}: {
  cameras: StoreCamera[]
  session: StoreCameraViewSession | undefined
  initialCameraName: string | null
  onClose: () => void
}) {
  const { data: me } = useQuery({ queryKey: ['store', 'me'], queryFn: meStore })
  // Pagination : 6 caméras par écran. On ouvre sur la page qui contient la caméra visée (chevron
  // sans caméra → page 0).
  const pages = useMemo(
    () =>
      Array.from({ length: Math.ceil(cameras.length / CAMERAS_PER_PAGE) }, (_, index) =>
        cameras.slice(index * CAMERAS_PER_PAGE, (index + 1) * CAMERAS_PER_PAGE),
      ),
    [cameras],
  )
  const pageCount = Math.max(1, pages.length)
  const [page, setPage] = useState(() => {
    const target = cameras.findIndex((camera) => camera.name === initialCameraName)
    return target !== -1 ? Math.floor(target / CAMERAS_PER_PAGE) : 0
  })
  const [now, setNow] = useState(() => DateTime.now())
  // Glissement en cours : décalage de la piste en px CSS (le canvas est mis à l'échelle, d'où la
  // conversion depuis les px écran du pointeur), nul au repos.
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const drag = useRef<{ pointerId: number; startX: number; startY: number; scale: number } | null>(
    null,
  )

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), CLOCK_TICK_MS)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      // Flèches : même navigation que le glissement, pour l'aperçu du BO au clavier.
      if (event.key === 'ArrowRight') setPage((current) => Math.min(pageCount - 1, current + 1))
      if (event.key === 'ArrowLeft') setPage((current) => Math.max(0, current - 1))
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose, pageCount])

  const timestamp = now.setZone('Europe/Paris').toFormat('HH:mm - dd.MM.yyyy')

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // Bouton principal seulement : un clic droit (menu contextuel) n'amorce pas de glissement.
    if (pageCount < 2 || event.button !== 0) return
    const viewport = event.currentTarget
    // Capture : le geste reste suivi même si le doigt sort de la grille.
    viewport.setPointerCapture(event.pointerId)
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scale: viewport.getBoundingClientRect().width / viewport.offsetWidth || 1,
    }
    setIsDragging(true)
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    if (current?.pointerId !== event.pointerId) return
    setDragX(swipeDragOffset(page, pageCount, event.clientX - current.startX) / current.scale)
  }

  // Fin du geste : la piste revient se caler sur une page (la voisine si le glissement suffit).
  // Une annulation (`pointercancel`, capture perdue) remet simplement la page courante.
  const endDrag = (event: PointerEvent<HTMLDivElement>, commit: boolean) => {
    const current = drag.current
    if (current?.pointerId !== event.pointerId) return
    drag.current = null
    setIsDragging(false)
    setDragX(0)
    if (commit) {
      const dx = event.clientX - current.startX
      const dy = event.clientY - current.startY
      setPage(pageAfterSwipe(page, pageCount, dx, dy))
    }
  }

  // Pages mémorisées : un glissement ne redessine que la piste, pas les lecteurs vidéo.
  const pageGrids = useMemo(
    () =>
      pages.map((pageCameras, pageIndex) => (
        <div
          key={pageIndex}
          className="grid w-full shrink-0 grid-cols-3 content-start"
          style={{ gap: PAGE_GAP_PX }}
        >
          {pageCameras.map((camera, index) => (
            <CameraTile
              key={camera.id}
              camera={camera}
              number={pageIndex * CAMERAS_PER_PAGE + index + 1}
              session={session}
              live={pageIndex === page}
              timestamp={timestamp}
            />
          ))}
        </div>
      )),
    [pages, page, session, timestamp],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#072C1A]/25 p-[32px]"
      role="dialog"
      aria-modal="true"
      aria-label="Caméras de la boutique"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-[1917px] flex-col gap-[16px] overflow-hidden rounded-[24px] bg-[#F0EDE2] p-[32px] drop-shadow-[0px_8px_20px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* En-tête : icône + titre à gauche, bouton fermer à droite. */}
        <div className="flex items-center gap-[24px]">
          <div className="flex min-w-0 flex-1 items-center gap-[12px]">
            <span className="flex size-[40px] shrink-0 items-center justify-center rounded-[12px] bg-[#F9F8F1] text-[#4F6A47]">
              <Camera01Icon className="size-[24px]" />
            </span>
            <h2 className="truncate font-brand text-[22px] leading-[26px] font-semibold text-[#4F6A47] uppercase">
              Caméras {me?.store.label ?? ''}
            </h2>
          </div>

          <ModalCloseButton onClose={onClose} ariaLabel="Fermer les caméras" />
        </div>

        <div className="h-px w-full shrink-0 bg-[#0000000F]" />

        {cameras.length > 0 ? (
          // 6 caméras par page (3 colonnes × 2 lignes), les pages côte à côte sur une piste qui
          // glisse. Tuiles au format 16:9 des flux : l'image entière, sans bandes noires ni rognage
          // (la modale fait ~2/3 de l'écran, comme le Figma). `touch-pan-y` : le navigateur laisse
          // le geste horizontal à la piste au lieu d'en faire un retour arrière.
          <div
            className="touch-pan-y overflow-hidden select-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={(event) => endDrag(event, true)}
            onPointerCancel={(event) => endDrag(event, false)}
            // Capture perdue sans `pointerup` (relâchement avalé) : la piste ne reste pas accrochée.
            onLostPointerCapture={(event) => endDrag(event, false)}
          >
            <div
              className={cn('flex', !isDragging && 'transition-transform duration-300 ease-out')}
              style={{
                gap: PAGE_GAP_PX,
                transform: `translateX(calc(${-page} * (100% + ${PAGE_GAP_PX}px) + ${dragX}px))`,
              }}
            >
              {pageGrids}
            </div>
          </div>
        ) : (
          <span className="py-[40px] text-center font-sans text-[16px] text-[#3E4F3F]">
            Aucune caméra sur cette boutique.
          </span>
        )}

        {pageCount > 1 ? (
          // Pagination (au-delà de 6 caméras) : une pastille par page, la page courante en vert.
          <div className="flex shrink-0 items-center justify-center gap-[10px]">
            {Array.from({ length: pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index)}
                aria-label={`Page ${index + 1} sur ${pageCount}`}
                aria-current={index === page}
                className={cn(
                  'size-[12px] rounded-full transition-colors',
                  index === page ? 'bg-[#4F6A47]' : 'bg-[#C7CFC4] hover:bg-[#9FB199]',
                )}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
